import React from 'react';
import { Formik, Form as FormikForm } from 'formik';
import { Row, Col, Divider, Alert } from 'antd'; 
import {
  expenseFormFields,
  expenseFormSchema,
  expenseFormInitialValues,
  expenseConfig, 
} from './expense.config'; 
import { useEntityForm } from '../../hooks/useEntityForm';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import { EntityFormLayout } from '../../components/form/EntityFormLayout';
import { DynamicFormFields } from '../../components/form/DynamicFormFields';
import { CreateExpenseRequest, Expense, UpdateExpenseRequest } from './expense.types';
import { useExpenseFormOptions } from '../../hooks/useFormOptions';

const ExpenseForm: React.FC = () => {
  const {
    id, mode, data, loading, handleSubmit, handleReset, navigate,
  } = useEntityForm<Expense, CreateExpenseRequest, UpdateExpenseRequest>({
    entityName: expenseConfig.entityName,
    getEntity: expenseConfig.api.getOne,
    createEntity: expenseConfig.api.create,
    updateEntity: expenseConfig.api.update,
    redirectPath: expenseConfig.redirectPath,
  });

  const formOptions: any = useExpenseFormOptions();

  const dynamicFields = expenseFormFields.map(field =>
    formOptions[field.name]
      ? { ...field, options: formOptions[field.name] }
      : field
  );

  // Group fields for custom layout
  const firstRowFields = dynamicFields.filter(field => 
    ['amount', 'date', 'title', 'details'].includes(field.name)
  );
  const categoryField = dynamicFields.find(field => field.name === 'category');
  const categoryLevelFields = dynamicFields.filter(field => 
    ['catL1', 'catL2', 'catL3', 'catL4'].includes(field.name)
  );

  const deleteHandler = useDeleteHandler();
  const handleDelete = () => id && deleteHandler({
    id,
    entityName: expenseConfig.entityName,
    deleteApi: expenseConfig.api.delete,
    redirectPath: expenseConfig.redirectPath,
  });

  // Custom submit handler to exclude category field from API payload
  const handleFormSubmit = async (values: any) => {
    // Remove category field from payload as it's only for auto-selection
    const { category, ...apiPayload } = values;
    await handleSubmit(apiPayload);
  };

  return (
    <div className="h-full flex flex-col">
      <Formik
        key={`${id}-${mode}`}
        enableReinitialize
        initialValues={expenseFormInitialValues(data)}
        validationSchema={expenseFormSchema}
        onSubmit={handleFormSubmit}
      >
        {({ dirty }) => (
          <FormikForm className="flex flex-col flex-grow h-full">
            <EntityFormLayout
              title={
                mode === 'create'
                  ? `Create ${expenseConfig.entityName}`
                  : mode === 'edit'
                    ? `Edit ${expenseConfig.entityName}`
                    : `View ${expenseConfig.entityName}`
              }
              mode={mode}
              data={data}
              loading={loading}
              dirty={dirty}
              onGoBack={() => navigate(expenseConfig.redirectPath)}
              onReset={handleReset}
              onDelete={handleDelete}
              onCreateNew={() => navigate(`${expenseConfig.redirectPath}/create`)}
              onEdit={() => navigate(`${expenseConfig.redirectPath}/edit/${id}`)}
              onView={() => navigate(`${expenseConfig.redirectPath}/view/${id}`)}
            >
              {/* First Row: Amount, Date, Title, Details */}
              <Row gutter={24}>
                {firstRowFields.map(field => {
                  const commonProps = {
                    ...field,
                    disabled: mode === 'view' || field.disabled,
                  };
                  const colProps = {
                    xs: field.xs || 24,
                    sm: field.sm || 12,
                    md: field.md || 12,
                    lg: field.lg || 12,
                    xl: field.xl || 12,
                  };

                  const { TextField } = require('../../components/form/fields/TextField');
                  const { NumberField } = require('../../components/form/fields/NumberField');
                  const { DateField } = require('../../components/form/fields/DateField');
                  const { TextareaField } = require('../../components/form/fields/TextareaField');

                  return (
                    <Col {...colProps} key={field.name}>
                      {field.type === 'text' && <TextField {...commonProps} />}
                      {field.type === 'number' && <NumberField {...commonProps} />}
                      {field.type === 'date' && <DateField {...commonProps} />}
                      {field.type === 'textarea' && <TextareaField {...commonProps} />}
                    </Col>
                  );
                })}
              </Row>

              {/* Separator with helpful message */}
              <div style={{ margin: '24px 0' }}>
                <Divider style={{ margin: '16px 0' }} />
                <div style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  marginBottom: '16px',
                  padding: '8px 12px',
                  backgroundColor: '#fafafa',
                  borderRadius: '4px',
                  border: '1px solid #f0f0f0'
                }}>
                  <strong>Quick Category Selection:</strong> Select a category combination below to automatically fill the 4 category levels. You can customize all 4 category levels manually if needed. This field is only for auto-fill and won't be saved to the database.
                </div>
              </div>

              {/* Category Selection Row */}
              {categoryField && (
                <Row gutter={24}>
                  <Col span={24}>
                    {(() => {
                      const { SelectField } = require('../../components/form/fields/SelectField');
                      const commonProps = {
                        ...categoryField,
                        disabled: mode === 'view' || categoryField.disabled,
                      };
                      return <SelectField {...commonProps} />;
                    })()}
                  </Col>
                </Row>
              )}

              {/* Category Levels Row */}
              <Row gutter={24} style={{ marginTop: '16px' }}>
                {categoryLevelFields.map(field => {
                  const commonProps = {
                    ...field,
                    disabled: mode === 'view' || field.disabled,
                  };
                  const colProps = {
                    xs: field.xs || 24,
                    sm: field.sm || 6,
                    md: field.md || 6,
                    lg: field.lg || 6,
                    xl: field.xl || 6,
                  };

                  const { TextField } = require('../../components/form/fields/TextField');

                  return (
                    <Col {...colProps} key={field.name}>
                      <TextField {...commonProps} />
                    </Col>
                  );
                })}
              </Row>
            </EntityFormLayout>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
};

export default ExpenseForm;

